// Renders a three.js scene into a linear depth map (1 = near).
//
// Depth is packed into two 8-bit channels (16-bit precision) so we do not
// depend on float render-target support.

import * as THREE from 'three';
import type { DepthData } from '../types';

const vertexShader = /* glsl */ `
  varying float vDepth;
  uniform int uMode;
  void main() {
    vec4 world = modelMatrix * vec4(position, 1.0);
    vec4 view = viewMatrix * world;
    // Mode 0: height along world +Z (orthographic relief), mode 1: view distance.
    vDepth = uMode == 0 ? world.z : -view.z;
    gl_Position = projectionMatrix * view;
  }
`;

const fragmentShader = /* glsl */ `
  precision highp float;
  varying float vDepth;
  uniform int uMode;
  uniform float uNear;   // value mapped to 1.0
  uniform float uFar;    // value mapped to 0.0
  uniform float uBase;
  uniform float uTop;
  void main() {
    float t = clamp((vDepth - uFar) / (uNear - uFar), 0.0, 1.0);
    float d = clamp(uBase + (uTop - uBase) * t, 0.0, 1.0);
    float v = floor(d * 65535.0 + 0.5);
    float hi = floor(v / 256.0);
    float lo = v - hi * 256.0;
    gl_FragColor = vec4(hi / 255.0, lo / 255.0, 0.0, 1.0);
  }
`;

export interface DepthRange {
  /** 'height': world Z is depth (near/far are Z values); 'view': distance from camera. */
  mode: 'height' | 'view';
  near: number;
  far: number;
  /** Output depth at `far` and `near` respectively. */
  base: number;
  top: number;
}

let renderer: THREE.WebGLRenderer | null = null;

function getRenderer(): THREE.WebGLRenderer {
  if (!renderer) {
    const canvas = document.createElement('canvas');
    renderer = new THREE.WebGLRenderer({ canvas, antialias: false, preserveDrawingBuffer: false });
    renderer.setPixelRatio(1);
  }
  return renderer;
}

export function createDepthMaterial(range: DepthRange): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    side: THREE.DoubleSide,
    uniforms: {
      uMode: { value: range.mode === 'height' ? 0 : 1 },
      uNear: { value: range.near },
      uFar: { value: range.far },
      uBase: { value: range.base },
      uTop: { value: range.top },
    },
  });
}

/**
 * Renders `scene` from `camera` at `width x height`, supersampling by `ss`
 * (box filtered) for smooth edges. Background depth is 0.
 */
export function renderDepth(
  scene: THREE.Scene,
  camera: THREE.Camera,
  range: DepthRange,
  width: number,
  height: number,
  ss = 2,
): DepthData {
  const r = getRenderer();
  const maxSize = r.capabilities.maxTextureSize;
  ss = Math.max(1, Math.min(ss, Math.floor(maxSize / Math.max(width, height))));
  const rw = width * ss;
  const rh = height * ss;
  const target = new THREE.WebGLRenderTarget(rw, rh, { depthBuffer: true, type: THREE.UnsignedByteType });
  const material = createDepthMaterial(range);
  const prevOverride = scene.overrideMaterial;
  const prevBg = scene.background;
  scene.overrideMaterial = material;
  scene.background = null;
  r.setRenderTarget(target);
  r.setClearColor(0x000000, 1);
  r.clear();
  r.render(scene, camera);
  const px = new Uint8Array(rw * rh * 4);
  r.readRenderTargetPixels(target, 0, 0, rw, rh, px);
  r.setRenderTarget(null);
  scene.overrideMaterial = prevOverride;
  scene.background = prevBg;
  target.dispose();
  material.dispose();

  const out = new Float32Array(width * height);
  const inv = 1 / (65535 * ss * ss);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let acc = 0;
      for (let sy = 0; sy < ss; sy++) {
        // GL rows start at the bottom.
        const row = rh - 1 - (y * ss + sy);
        for (let sx = 0; sx < ss; sx++) {
          const i = (row * rw + x * ss + sx) * 4;
          acc += px[i] * 256 + px[i + 1];
        }
      }
      out[y * width + x] = acc * inv;
    }
  }
  return { width, height, data: out };
}
